import type { PortalStage } from '../types';

const ORDER: PortalStage[] = [
  'design-fee',
  '3d-model',
  'quote',
  'quote-signed',
  '2d-plans',
  'in-production',
  'delivery',
];

function maxStage(a: PortalStage, b: PortalStage): PortalStage {
  const ia = ORDER.indexOf(a);
  const ib = ORDER.indexOf(b);
  if (ia < 0) return b;
  if (ib < 0) return a;
  return ia >= ib ? a : b;
}

/** True when the portal project has a released or uploaded 3D model for clients to view. */
export function portalHasModelContent(
  project: {
    modelUrl?: string;
    modelImageUrl?: string;
    modelLoomUrl?: string;
    modelStepSkipped?: boolean;
  } | null | undefined,
): boolean {
  if (!project || project.modelStepSkipped) return false;
  return !!(
    project.modelUrl?.trim() ||
    project.modelImageUrl?.trim() ||
    project.modelLoomUrl?.trim()
  );
}

/** Bump display stage to at least 3d-model when model content exists but CRM stage was not advanced yet. */
export function portalStageWithReleasedModel(
  stored: PortalStage,
  project: Parameters<typeof portalHasModelContent>[0],
): PortalStage {
  if (!portalHasModelContent(project)) return stored;
  return maxStage(stored, '3d-model');
}

/** Bump display stage to at least quote when a proposal has been sent to the client. */
export function portalStageWithSentProposal(
  stored: PortalStage,
  proposalStatus?: string | null,
): PortalStage {
  if (!proposalStatus || !['sent', 'opened', 'approved'].includes(proposalStatus)) return stored;
  return maxStage(stored, 'quote');
}

/**
 * Portal `currentStage` in Firestore can lag behind CRM (e.g. deal marked won or proposal approved
 * before multi-project portal fields were updated). Bump display progress to at least `quote-signed`
 * when the deal is won or the proposal is approved so internal/public timelines stay in sync.
 */
export function effectivePortalStageForProgress(
  stored: PortalStage,
  opts: {
    dealOutcome?: 'won' | 'lost' | 'active';
    proposalApproved?: boolean;
  },
): PortalStage {
  if (opts.dealOutcome === 'lost') return stored;
  if (opts.dealOutcome !== 'won' && !opts.proposalApproved) return stored;
  return maxStage(stored, 'quote-signed');
}
